import React, { FC, HTMLAttributes } from 'react';
import classNames from 'classnames';
import { t } from 'utils';
import { ImagePreview } from 'components';
import { FileInput } from 'components/forms';
import { Note } from 'components/ui';
import { useField } from 'react-final-form';

import styles from './LogoUploader.module.scss';
import { TranslateReplaceItem } from 'utils/translation';
import { PreviewSizeSelector } from 'components/ImagePreview/ImagePreview';

export interface ILogoUploader extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  isFormSubmitSuccess: boolean;
  placeholder: string;
  label: string;
  acceptFileTypes: string;
  imageFieldName: string;
  note?: string;
  allowedWidth: number;
  allowedHeight: number;
  notePlaceholders?: TranslateReplaceItem[];
  previewSize: PreviewSizeSelector;
  fileInputClassName?: string;
}

type CheckSize = { [x: string]: string } | undefined;

const LogoUploader: FC<ILogoUploader> = ({
  note,
  label,
  disabled,
  className,
  placeholder,
  previewSize,
  allowedWidth,
  allowedHeight,
  imageFieldName,
  acceptFileTypes,
  notePlaceholders,
  fileInputClassName,
  isFormSubmitSuccess,
}) => {
  const classes = classNames(styles['ta-logo-uploader'], className, {
    [styles.disabled]: disabled,
  });
  const fileInputClasses = classNames(
    styles['ta-logo-uploader__input-container'],
    fileInputClassName
  );

  const checkLogoSize = async (src: string): Promise<CheckSize> => {
    if (!src) {
      return;
    }

    const image = new Image();

    const promise = new Promise<CheckSize>((resolve, reject) => {
      image.onload = () => {
        const { width, height } = image;

        if (width > allowedWidth || height > allowedHeight) {
          return resolve({ [imageFieldName]: 'logo.size.is.wrong' });
        }

        resolve(undefined);
      };

      image.onerror = reject;
    });

    image.src = src;

    return await promise;
  };

  const { input, meta } = useField(imageFieldName, { validate: checkLogoSize });

  return (
    <div className={classes}>
      <ImagePreview size={previewSize} image={input.value} />
      <div className={fileInputClasses}>
        <FileInput
          disabled={disabled}
          onChange={input.onChange}
          onBlur={input.onBlur}
          isError={meta.error && meta.touched}
          isSuccess={isFormSubmitSuccess ? !meta.error && meta.touched : false}
          placeholder={t(placeholder)}
          label={t(label)}
          acceptFileTypes={acceptFileTypes}
        />
        {note && <Note>{t(note, notePlaceholders)}</Note>}
      </div>
    </div>
  );
};

export default LogoUploader;
